import { MutableRefObject, PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
	Anchor,
	DRAG_THRESHOLD,
	LAUNCHER_BOX,
	Margins,
	Rect,
	Size,
	Viewport,
	anchorFromSettings,
	hitZones,
	isDraggablePosition,
	launcherRect,
	marginsFromSettings,
	parseAnchor,
	placeSurface,
	positionFromBox,
	positionStorageKey,
} from '../../common/geometry';
import createLogger from '../../common/logger';
import { persistAnchor, readLocalAnchor, requestParentAnchor } from '../../common/positionStorage';

const logger = createLogger('geometry');

/** 拖動中多久沒收到指標事件就自行釋放，避免熱區永久停在全屏 */
const DRAG_WATCHDOG_MS = 8000;

/**
 * 宿主頁的可視範圍。
 *
 * iframe 永遠佔滿可視範圍（見 livechat-iframe.css），所以這裡量到的就是宿主頁的尺寸，
 * 不需要宿主頁把 viewport 傳進來。
 */
const readViewport = (): Viewport => ({
	width: window.innerWidth || document.documentElement.clientWidth,
	height: window.innerHeight || document.documentElement.clientHeight,
});

const postHitZones = (rects: Rect[]) => {
	window.parent.postMessage({ target: 'eightChat', command: 'setHitZones', options: { rects } }, '*');
};

export interface WidgetGeometryInput {
	orgId: string;
	styleData: any;
	/** 目前浮層（面板／通知）的尺寸；null 表示沒有浮層 */
	surfaceSize: Size | null;
	/**
	 * 浮層可接受的最低高度 —— 面板由 App 量測「標題列 + rich menu + 輸入列」得出。
	 *
	 * 省略時以浮層自身高度為底（不接受任何壓縮），通知泡泡走這條。
	 */
	surfaceMinHeight?: number;
	/** 展開模式、圖片檢視、手機面板開啟 —— 熱區涵蓋整個可視範圍 */
	fullscreen: boolean;
	/**
	 * 放開後補間到最終位置的時長（`prefers-reduced-motion` 下為 0）。
	 *
	 * 這段期間熱區要維持全屏 —— 否則 launcher 補間中會短暫落在熱區之外，被 clip-path
	 * 連畫面一起裁掉。
	 */
	settleMs: number;
	/**
	 * 位移未達門檻放開時觸發 —— 也就是「點擊 launcher」。
	 *
	 * 刻意不讓 launcher 掛 onClick：click 會在 pointerup 之後照樣發出，拖動完還要另外
	 * 想辦法把它壓掉。由 pointerup 這裡判定就沒有這個問題。
	 */
	onLauncherTap: () => void;
}

export interface WidgetGeometry {
	viewport: Viewport;
	anchor: Anchor;
	margins: Margins;
	launcher: Rect;
	surfaceRect: Rect | null;
	isDragging: boolean;
	/** 後台是否允許使用者拖動（position === 'freedom'） */
	isDraggable: boolean;
	startDrag: (event: ReactPointerEvent) => void;
	/**
	 * 拖動期間直接寫這兩個元素的 inline style，繞過 React。
	 *
	 * 每個 pointermove 都跑一次 React render 會連帶重繪整棵 Chat（它沒有 memo，而 App
	 * 傳下去的 callback 每次 render 都是新的）—— 實測面板開啟時每次移動 3.2ms，關閉時
	 * 只要 0.3ms。改成命令式寫入後只有 pointerdown/pointerup 各一次 render。
	 */
	launcherElRef: MutableRefObject<HTMLElement | null>;
	surfaceElRef: MutableRefObject<HTMLElement | null>;
}

/**
 * widget 的定位中樞。
 *
 * 只有一個座標系 —— iframe 就是宿主頁的可視範圍，所以 launcher 與浮層直接用算出來的
 * 座標定位，不需要任何「在框內貼哪一邊」的偏移或畫布補償。
 */
const useWidgetGeometry = ({
	orgId,
	styleData,
	surfaceSize,
	surfaceMinHeight,
	fullscreen,
	settleMs,
	onLauncherTap,
}: WidgetGeometryInput): WidgetGeometry => {
	const [viewport, setViewport] = useState<Viewport>(readViewport);
	const [storedAnchor, setStoredAnchor] = useState<Anchor | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [isSettling, setIsSettling] = useState(false);

	const margins = useMemo<Margins>(() => marginsFromSettings(styleData), [styleData]);
	const settingsAnchor = useMemo<Anchor>(() => anchorFromSettings(styleData), [styleData]);

	/** 只有後台設為 `freedom` 才允許拖動 */
	const isDraggable = useMemo(() => isDraggablePosition(styleData), [styleData]);

	// 可拖動時使用者存的位置優先；不可拖動時一律用後台設定（即使存過位置也忽略），
	// 否則後台改回 bottom-right/bottom-left 就無法「維持原來的擺放位置」
	const anchor = isDraggable && storedAnchor ? storedAnchor : settingsAnchor;

	useEffect(() => {
		// readViewport 每次都回傳新物件，直接 setState 會讓每個 resize 事件都 re-render
		// 整個 widget —— 而 resize 在拖視窗邊框時一秒好幾十次，行動瀏覽器捲動時也常送出
		// 尺寸沒變的 resize。比對過再換，沒變就回同一個物件讓 React 略過。
		const sync = () =>
			setViewport(previous => {
				const next = readViewport();
				return previous.width === next.width && previous.height === next.height ? previous : next;
			});
		window.addEventListener('resize', sync);
		window.addEventListener('orientationchange', sync);
		return () => {
			window.removeEventListener('resize', sync);
			window.removeEventListener('orientationchange', sync);
		};
	}, []);

	// 位置的讀取：localStorage 是同步的，首次 render 就有值；被封鎖時才問宿主頁 cookie
	useEffect(() => {
		if (!orgId || !isDraggable) {
			return;
		}

		const local = readLocalAnchor(orgId);
		if (local) {
			setStoredAnchor(local);
			return;
		}

		requestParentAnchor(orgId);
	}, [orgId, isDraggable]);

	// 宿主頁 cookie 的回應，依 cookie 名稱分流
	useEffect(() => {
		if (!orgId || !isDraggable) {
			return undefined;
		}

		const key = positionStorageKey(orgId);
		const onMessage = (event: MessageEvent) => {
			const { data } = event;
			if (data?.target !== 'eightChat' || data.command !== 'getParentCookie') {
				return;
			}
			if (data.options?.name !== key) {
				return;
			}

			const parsed = parseAnchor(data.options?.value);
			if (parsed) {
				setStoredAnchor(parsed);
			}
		};

		window.addEventListener('message', onMessage);
		return () => window.removeEventListener('message', onMessage);
	}, [orgId, isDraggable]);

	const launcher = useMemo<Rect>(() => launcherRect(anchor, viewport, margins), [anchor, viewport, margins]);

	const surfaceRect = useMemo<Rect | null>(
		() => (surfaceSize ? placeSurface(launcher, surfaceSize, viewport, margins, surfaceMinHeight) : null),
		[launcher, surfaceSize, surfaceMinHeight, viewport, margins]
	);

	const zones = useMemo(
		// 拖動期間熱區涵蓋整個可視範圍：命中測試在宿主頁先發生，指標一離開熱區就收不到事件。
		// 補間期間也維持全屏，否則移動中的 launcher 會被 clip-path 裁掉。
		() => hitZones(viewport, launcher, surfaceRect, fullscreen || isDragging || isSettling),
		[viewport, launcher, surfaceRect, fullscreen, isDragging, isSettling]
	);

	const lastSentRef = useRef('');
	useEffect(() => {
		// 後台設定還沒回來時 widget 什麼都不渲染，這時候不要回報熱區 —— CSS 預設是不裁出
		// 任何區域，宿主頁完全不受影響
		if (!styleData) {
			return;
		}

		const serialized = JSON.stringify(zones);
		if (serialized === lastSentRef.current) {
			return;
		}
		lastSentRef.current = serialized;
		postHitZones(zones);
	}, [zones, styleData]);

	const dragRef = useRef<{
		pointerId: number;
		startX: number;
		startY: number;
		originTop: number;
		originLeft: number;
		/** 按下的點在 launcher 內的相對位置 —— 校準時要保留，launcher 才會貼在手上 */
		grabX: number;
		grabY: number;
		moved: boolean;
		/** 是否已用真正收到的第一個 move 校準過基準 */
		armed: boolean;
	} | null>(null);

	// pointermove/up 的 handler 需要當下的 viewport、margins 與 tap callback，
	// 但不該因為它們變動就重建 handler（會在拖動中途掉事件）
	const contextRef = useRef({ viewport, margins, orgId, onLauncherTap, settleMs, surfaceSize, surfaceMinHeight });
	contextRef.current = { viewport, margins, orgId, onLauncherTap, settleMs, surfaceSize, surfaceMinHeight };

	const launcherElRef = useRef<HTMLElement | null>(null);
	const surfaceElRef = useRef<HTMLElement | null>(null);

	/** 拖動中的 listener 解除函式 —— 同步註冊、同步解除，不經過 render */
	const detachRef = useRef<(() => void) | null>(null);

	const startDrag = useCallback(
		(event: ReactPointerEvent) => {
			// 只吃主鍵，右鍵／中鍵不啟動拖動
			if (event.button !== 0) {
				return;
			}

			const drag = {
				pointerId: event.pointerId,
				// iframe 就是可視範圍，所以 clientX/clientY 直接是宿主頁座標
				startX: event.clientX,
				startY: event.clientY,
				lastX: event.clientX,
				lastY: event.clientY,
				originTop: launcher.top,
				originLeft: launcher.left,
				// 按下的點在 launcher 內的相對位置 —— 校準時要保留，launcher 才會貼在手上
				grabX: event.clientX - launcher.left,
				grabY: event.clientY - launcher.top,
				moved: false,
				armed: false,
			};
			dragRef.current = drag;

			try {
				(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
			} catch (e) {
				// 少數瀏覽器在同步呼叫下會拋，退回 window listener 也能收到事件
			}

			const listeners: Array<[string, EventListener, AddEventListenerOptions?]> = [];
			let watchdog: ReturnType<typeof setTimeout> | null = null;
			let done = false;

			/**
			 * 直接把新位置寫進 DOM，不經過 React。
			 *
			 * 只碰 transform 與 top/left/width/maxHeight，都是 O(1) 的樣式寫入；不讀任何
			 * 幾何屬性，所以不會造成強制回流。放開時才 setStoredAnchor 讓 React 收斂，
			 * 屆時 React 會用 state 算出的值覆寫這裡寫的 inline style。
			 */
			const paint = (box: { top: number; left: number }) => {
				const { viewport: vp, margins: mg, surfaceSize: size, surfaceMinHeight: floor } = contextRef.current;

				if (launcherElRef.current) {
					launcherElRef.current.style.transform = `translate3d(${box.left}px, ${box.top}px, 0)`;
				}

				const surfaceEl = surfaceElRef.current;
				if (!surfaceEl || !size) {
					return;
				}

				const rect = placeSurface(
					{ top: box.top, left: box.left, width: LAUNCHER_BOX, height: LAUNCHER_BOX },
					size,
					vp,
					mg,
					floor
				);
				surfaceEl.style.top = `${rect.top}px`;
				surfaceEl.style.left = `${rect.left}px`;
				surfaceEl.style.width = `${rect.width}px`;
				surfaceEl.style.height = `${rect.height}px`;
			};

			const detach = () => {
				listeners.forEach(([type, handler, options]) => window.removeEventListener(type, handler, options));
				listeners.length = 0;
				if (watchdog) {
					clearTimeout(watchdog);
					watchdog = null;
				}
				detachRef.current = null;
			};

			const finish = (cancelled: boolean) => {
				if (done) {
					return;
				}
				done = true;
				detach();

				const {
					viewport: currentViewport,
					margins: currentMargins,
					orgId: currentOrgId,
					onLauncherTap: tap,
					settleMs: currentSettleMs,
				} = contextRef.current;

				if (drag.moved) {
					const box = {
						top: drag.originTop + (drag.lastY - drag.startY),
						left: drag.originLeft + (drag.lastX - drag.startX),
					};
					const dropped = positionFromBox(box, currentViewport, currentMargins);
					setStoredAnchor(dropped);

					if (currentOrgId) {
						const via = persistAnchor(currentOrgId, dropped);
						logger.log('launcher position saved', { ...dropped, via });
					}
				}

				dragRef.current = null;
				setIsDragging(false);

				// 落點被夾回畫面內時位置會跳一段，補間期間熱區維持全屏
				if (drag.moved && currentSettleMs > 0) {
					setIsSettling(true);
					setTimeout(() => setIsSettling(false), currentSettleMs);
				}

				// 沒位移就是點擊。被取消（系統手勢打斷、看門狗）不算點擊
				if (!drag.moved && !cancelled) {
					tap();
				}
			};

			/**
			 * 看門狗。
			 *
			 * pointerup 一旦因任何原因沒送到（分頁切換、系統手勢打斷、瀏覽器介入），
			 * isDragging 會永久停在 true、熱區停在全屏，使用者的整個頁面就點不動而且無法
			 * 自行恢復 —— 後果太重，所以用時間兜底，而不是依賴 lostpointercapture
			 * （合成事件下根本不會有指標捕獲，那條路救不到）。
			 */
			const armWatchdog = () => {
				if (watchdog) {
					clearTimeout(watchdog);
				}
				watchdog = setTimeout(() => {
					logger.warn('drag watchdog fired, releasing');
					finish(true);
				}, DRAG_WATCHDOG_MS);
			};

			const onMove = (moveEvent: globalThis.PointerEvent) => {
				if (moveEvent.pointerId !== drag.pointerId) {
					return;
				}
				armWatchdog();

				drag.lastX = moveEvent.clientX;
				drag.lastY = moveEvent.clientY;

				// 熱區改成全屏是走 postMessage 的非同步變更；在宿主頁套用之前，裁切區還只有
				// launcher 那 80x80，指標一移出去，pointermove 就落到宿主頁、這裡收不到。
				// 所以用真正收到的第一個 move 重新校準：把原點設成「指標當下位置減去抓取偏移」，
				// launcher 就會貼在手上（正常情況第一個 move 只差不到 1px，看不出差別；掉了一
				// 大段事件時 launcher 會一次補到游標下，而不是整段拖動都差一個固定距離）。
				if (!drag.armed) {
					drag.armed = true;
					drag.startX = moveEvent.clientX;
					drag.startY = moveEvent.clientY;
					drag.originLeft = moveEvent.clientX - drag.grabX;
					drag.originTop = moveEvent.clientY - drag.grabY;
				}

				const deltaX = moveEvent.clientX - drag.startX;
				const deltaY = moveEvent.clientY - drag.startY;

				if (!drag.moved && Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) {
					return;
				}
				drag.moved = true;

				paint({ top: drag.originTop + deltaY, left: drag.originLeft + deltaX });
			};

			const onUp = (endEvent: globalThis.PointerEvent) => {
				if (endEvent.pointerId !== drag.pointerId) {
					return;
				}
				drag.lastX = endEvent.clientX;
				drag.lastY = endEvent.clientY;
				finish(false);
			};

			const onCancel = (endEvent: globalThis.PointerEvent) => {
				if (endEvent.pointerId !== drag.pointerId) {
					return;
				}
				// 觸控上這是最可能的失敗點：瀏覽器判定手勢是捲動就會取消指標，拖動當場中斷
				logger.warn('drag cancelled by browser', { type: endEvent.type, pointerType: endEvent.pointerType });
				finish(true);
			};

			/**
			 * 拖動期間吃掉 touchmove 的預設行為，也就是宿主頁的捲動。
			 *
			 * launcher 上的 `touch-action: none` 在同一份文件裡就夠了，但這裡是 iframe ——
			 * 跨文件的 touch-action 傳遞各家瀏覽器實作不一致（WebKit 尤其），一旦沒生效，
			 * 瀏覽器會把手指的移動判定成捲動宿主頁並丟出 pointercancel，拖動根本開始不了。
			 * touchmove 的 preventDefault 不依賴那條路：事件在 iframe 內就被取消，宿主頁
			 * 的捲動也一起取消。必須 passive: false，否則 preventDefault 無效。
			 */
			const onTouchMove = (touchEvent: Event) => {
				if (touchEvent.cancelable) {
					touchEvent.preventDefault();
				}
			};

			// 必須在這裡同步註冊，不能等 isDragging 的 effect —— pointerup 可能在 React
			// re-render 之前就發生，那一掉就 isDragging 卡在 true。
			listeners.push(['pointermove', onMove as EventListener]);
			listeners.push(['pointerup', onUp as EventListener]);
			listeners.push(['pointercancel', onCancel as EventListener]);
			listeners.push(['lostpointercapture', onCancel as EventListener]);
			listeners.push(['touchmove', onTouchMove as EventListener, { passive: false }]);
			listeners.forEach(([type, handler, options]) => window.addEventListener(type, handler, options));
			detachRef.current = detach;
			armWatchdog();

			// 熱區涵蓋整個可視範圍，後續的指標移動才不受 launcher 邊界限制
			setIsDragging(true);
		},
		[launcher.top, launcher.left]
	);

	// 卸載時若還在拖動，把 listener 收乾淨
	useEffect(
		() => () => {
			if (detachRef.current) {
				detachRef.current();
			}
		},
		[]
	);

	return {
		viewport,
		anchor,
		margins,
		launcher,
		surfaceRect,
		isDragging,
		isDraggable,
		startDrag,
		launcherElRef,
		surfaceElRef,
	};
};

export default useWidgetGeometry;
